import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Users, 
  Globe,
  Zap,
  Bot,
  Sparkles,
  Star,
  Wand2
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { BotData } from "@/lib/bot-constants";

interface GameModeActionsProps {
  onEngineMode: () => void;
  onShowAISquad: () => void;
}

export function GameModeActions({ onEngineMode, onShowAISquad }: GameModeActionsProps) {
  const navigate = useNavigate();

  return (
    <div className="w-full max-w-5xl mx-auto">
      <div className="text-center mb-8">
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
          Choose Your Game Mode 🎯
        </h2>
        <p className="text-muted-foreground">
          What kind of chess adventure are you ready for?
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Prismatic Aurora Hatch Evolution Card */}
        <Card className="group relative cursor-pointer overflow-hidden transition-all duration-500 bg-gradient-to-br from-teal-100 via-emerald-100 via-50% to-cyan-100 dark:from-violet-900 dark:via-fuchsia-800 dark:via-50% dark:to-cyan-900 border-2 border-primary/40 hover:border-accent/60 hover:scale-[1.03] dark:animate-aurora-glow">
          
          {/* Rainbow shimmer border overlay - dark mode only */}
          <div className="hidden dark:block absolute inset-0 rounded-lg pointer-events-none">
            <div className="absolute inset-0 bg-gradient-to-r from-pink-500/20 via-yellow-400/20 via-cyan-400/20 to-purple-500/20 animate-shimmer" style={{ backgroundSize: '200% 100%' }} />
          </div>
          
          {/* Colorful floating sparkles/stars - dark mode only */}
          <div className="hidden dark:block absolute inset-0 overflow-hidden pointer-events-none">
            <Sparkles className="absolute top-3 right-4 w-4 h-4 text-cyan-300/80 animate-sparkle" />
            <Star className="absolute top-8 left-6 w-3 h-3 text-pink-400/70 animate-sparkle" style={{ animationDelay: '0.5s' }} />
            <Sparkles className="absolute bottom-12 right-8 w-3 h-3 text-yellow-300/60 animate-sparkle" style={{ animationDelay: '1s' }} />
            <Star className="absolute bottom-6 left-10 w-2 h-2 text-emerald-300/50 animate-sparkle" style={{ animationDelay: '1.5s' }} />
            <Star className="absolute top-1/2 right-12 w-2 h-2 text-purple-400/60 animate-sparkle" style={{ animationDelay: '0.7s' }} />
            <Sparkles className="absolute bottom-20 left-6 w-2 h-2 text-cyan-400/50 animate-sparkle" style={{ animationDelay: '1.2s' }} />
          </div>
          
          {/* Prismatic Badge */}
          <div className="absolute -top-1 -right-1 z-10">
            <Badge className="bg-gradient-to-r from-primary to-accent text-white dark:from-pink-400 dark:via-yellow-400 dark:to-cyan-400 dark:text-black font-bold text-xs px-2 py-0.5 shadow-lg animate-pulse">
              ✨ NEW
            </Badge>
          </div>

          <CardContent className="relative p-6 text-center z-10">
            <div className="space-y-4">
              {/* Prismatic Icon Constellation */}
              <div className="relative mx-auto w-20 h-20">
                {/* Rainbow rotating ring - dark mode only */}
                <div className="hidden dark:block absolute inset-0 animate-constellation-rotate opacity-40">
                  <Star className="absolute top-0 left-1/2 -translate-x-1/2 w-3 h-3 text-cyan-300 drop-shadow-[0_0_4px_rgba(34,211,238,0.8)]" />
                  <Star className="absolute bottom-0 left-1/2 -translate-x-1/2 w-3 h-3 text-yellow-300 drop-shadow-[0_0_4px_rgba(253,224,71,0.8)]" />
                  <Star className="absolute left-0 top-1/2 -translate-y-1/2 w-3 h-3 text-emerald-300 drop-shadow-[0_0_4px_rgba(52,211,153,0.8)]" />
                  <Star className="absolute right-0 top-1/2 -translate-y-1/2 w-3 h-3 text-pink-400 drop-shadow-[0_0_4px_rgba(244,114,182,0.8)]" />
                </div>
                
                {/* Prismatic central orb */}
                <div className="absolute inset-2 rounded-full bg-gradient-to-br from-primary via-accent to-primary dark:from-pink-500 dark:via-purple-500 dark:via-cyan-500 dark:to-emerald-400 shadow-lg dark:shadow-[0_0_30px_rgba(236,72,153,0.5),0_0_50px_rgba(34,211,238,0.4),0_0_70px_rgba(168,85,247,0.3)] flex items-center justify-center">
                  <Wand2 className="w-8 h-8 text-white dark:text-yellow-100 dark:drop-shadow-[0_0_10px_rgba(253,224,71,0.9)]" />
                </div>
              </div>
              
              {/* Rainbow Gradient Title */}
              <div>
                <CardTitle className="text-xl font-bold bg-gradient-to-r from-primary via-accent to-primary dark:from-cyan-200 dark:via-pink-300 dark:via-yellow-200 dark:to-purple-300 bg-clip-text text-transparent drop-shadow-sm">
                  Hatch Evolution
                </CardTitle>
                <CardDescription className="mt-2 text-foreground/80 dark:text-cyan-100/90">
                  ✨ Pieces transform through prismatic capture! 🌈
                </CardDescription>
              </div>
              
              {/* Rainbow Buttons */}
              <div className="space-y-2 pt-2">
                <Button 
                  onClick={() => navigate('/?mode=hatch')}
                  className="w-full bg-gradient-to-r from-primary via-accent to-primary dark:from-pink-600 dark:via-purple-600 dark:to-cyan-600 hover:from-primary/90 hover:to-accent/90 dark:hover:from-pink-500 dark:hover:via-purple-500 dark:hover:to-cyan-500 text-white font-semibold shadow-lg transition-all duration-300 border border-primary/30"
                >
                  <Sparkles className="w-4 h-4 mr-2" />
                  Enter the Realm 🌟
                </Button>
                <Button 
                  onClick={() => navigate('/hatch-rules')}
                  variant="ghost"
                  size="sm"
                  className="w-full text-foreground dark:text-cyan-200 hover:text-foreground dark:hover:text-cyan-100 hover:bg-muted dark:hover:bg-purple-800/30"
                >
                  📜 Ancient Rules
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Engine Analysis */}
        <Card className="group cursor-pointer transition-all duration-300 hover:scale-105 border-teen-purple/30 bg-gradient-to-br from-teen-purple/10 to-teen-purple/5 hover:border-teen-purple/50">
          <CardContent className="p-6 text-center">
            <div className="space-y-4">
              <div className="p-4 bg-teen-purple/20 rounded-full mx-auto w-fit">
                <Zap className="w-8 h-8 text-teen-purple" />
              </div>
              <div>
                <CardTitle className="text-xl text-teen-purple mb-2">Engine Analysis</CardTitle>
                <CardDescription className="mb-4 text-muted-foreground">
                  Analyze positions like a pro! ⚡
                </CardDescription>
              </div>
              <Button 
                onClick={onEngineMode}
                className="w-full bg-teen-purple hover:bg-teen-purple/90 text-white"
              >
                Start Engine! 🔥
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Play Online */}
        <Card className="group cursor-pointer transition-all duration-300 hover:scale-105 border-teen-blue/30 bg-gradient-to-br from-teen-blue/10 to-teen-blue/5 hover:border-teen-blue/50">
          <CardContent className="p-6 text-center">
            <div className="space-y-4">
              <div className="p-4 bg-teen-blue/20 rounded-full mx-auto w-fit">
                <Globe className="w-8 h-8 text-teen-blue" />
              </div>
              <div>
                <CardTitle className="text-xl text-teen-blue mb-2">Play Online</CardTitle>
                <CardDescription className="mb-4 text-muted-foreground">
                  Challenge players worldwide in real-time matches! 🌍
                </CardDescription>
              </div>
              <Button 
                onClick={() => navigate('/play-online')}
                className="w-full bg-teen-blue hover:bg-teen-blue/90 text-white"
              >
                Find Match! 🎯
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Challenge AI Squad */}
        <Card className="group cursor-pointer transition-all duration-300 hover:scale-105 border-teen-green/30 bg-gradient-to-br from-teen-green/10 to-teen-green/5 hover:border-teen-green/50">
          <CardContent className="p-6 text-center">
            <div className="space-y-4">
              <div className="p-4 bg-teen-green/20 rounded-full mx-auto w-fit">
                <Bot className="w-8 h-8 text-teen-green" />
              </div>
              <div>
                <CardTitle className="text-xl text-teen-green mb-2">Challenge AI Squad</CardTitle>
                <CardDescription className="mb-4 text-muted-foreground">
                  Battle our smart AI opponents! 🤖
                </CardDescription>
              </div>
              <Button 
                onClick={onShowAISquad}
                className="w-full bg-teen-green hover:bg-teen-green/90 text-white"
              >
                See AI Squad! 🚀
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}